// src/pages/UserDashboard.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useUser } from "../context/UserContext";

/* ───────── Data ───────── */
const FEATURED = [
  {
    key: "weddings",
    title: "Weddings & Events",
    sub: "Multiple cars. One checkout.",
    image: "/weddings.png",
  },
  {
    key: "corporate",
    title: "Corporate travel",
    sub: "Plan, review, finalize fast.",
    image: "/corporate.png",
  },
  {
    key: "vacations",
    title: "Family vacations",
    sub: "Outstation & multi-day made easy.",
    image: "/vacations5.png",
  },
];

const WHY_ITEMS = [
  {
    key: "reserveMany",
    title: "Reserve many at once",
    sub: "Add multiple vehicles into one plan & checkout together.",
    image: "/why-reserve-many.png",
  },
  {
    key: "exactCar",
    title: "Exact car, not just class",
    sub: "Pick the specific vehicle you want for your dates.",
    image: "/why-exact-car.png",
  },
  {
    key: "singleCheckout",
    title: "Single checkout for all",
    sub: "One payment flow for every vehicle you add.",
    image: "/why-one-payment.png",
  },
  {
    key: "timingRoutes",
    title: "Precise timing & routes",
    sub: "Round-trips, one-ways and optional stops in one flow.",
    image: "/why-precision.png",
  },
  {
    key: "nowLater",
    title: "Book now or schedule ahead",
    sub: "Instant or scheduled trips — both supported.",
    image: "/why-now-later.png",
  },
  {
    key: "pricing",
    title: "Transparent pricing",
    sub: "Clear breakup for base, fuel, toll and taxes.",
    image: "/why-pricing.png",
  },
];

/* Lightweight section wrapper */
function SectionShell({ title, eyebrow, right, children }) {
  return (
    <section className="mt-7 sm:mt-10">
      <div className="flex items-baseline justify-between gap-4">
        <div>
          {eyebrow && (
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
              {eyebrow}
            </div>
          )}
          <h2 className="mt-1 text-xl md:text-2xl font-semibold text-slate-900">
            {title}
          </h2>
        </div>
        {right}
      </div>
      <div className="mt-5">{children}</div>
    </section>
  );
}

/* ───────── Page ───────── */
export default function UserDashboard() {
  const { user } = useUser();
  const navigate = useNavigate();
  const isSignedIn = !!(user?.email || user?.phoneNumber);

  const displayName =
    user?.userName || user?.name || user?.firstName || "there";

  const handleSearchClick = () => {
    navigate("/discover");
  };

  const handleRideNowClick = () => {
    navigate("/ride-now");
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Header />

      <main className="bg-white">
        <div className="mx-auto max-w-[1560px] 2xl:max-w-[1760px] px-4 py-5 sm:px-6 sm:py-8 xl:px-10 2xl:px-12 md:py-10">
   {/* ───── HERO: greeting + single CTA ───── */}
<section className="relative overflow-hidden rounded-none border-0 bg-white px-0 py-1 shadow-none sm:rounded-3xl sm:border sm:border-slate-100 sm:bg-white sm:px-6 sm:py-6 sm:shadow-sm">
  {/* Subtle diamond motif on the right */}
  <div className="pointer-events-none absolute inset-y-0 right-0 hidden lg:block">
    <div className="absolute -right-10 top-4 h-44 w-44 rotate-45 rounded-3xl border border-slate-100" />
    <div className="absolute right-10 top-16 h-32 w-32 rotate-45 rounded-3xl border border-slate-100" />
  </div>

  <div className="relative flex flex-col gap-3 px-0 py-0 sm:gap-4 lg:flex-row lg:items-center lg:justify-between">
    {/* Left: title + greeting */}
    <div className="min-w-0">
      <div className="mb-3 h-1 w-14 rounded-full bg-[rgba(255,45,131,0.18)] sm:hidden" />
      <p className="hidden text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 sm:block">
        Start your plan
      </p>
      <h1 className="hidden mt-2 text-2xl font-extrabold tracking-tight text-slate-900 md:text-3xl sm:block">
        Welcome,{" "}
        <span className="text-[color:var(--brand-pink,#FF2D83)]">
          {isSignedIn ? displayName : "there"}
        </span>
      </h1>
      <div className="sm:hidden">
        <h1 className="text-[28px] font-bold leading-tight tracking-tight text-slate-900">
          Start your plan
        </h1>
        <p className="mt-3 text-[15px] font-medium text-slate-700">
          One or multiple cars in single trip
        </p>
      </div>
      <p className="mt-2 hidden max-w-xl text-sm text-slate-600 md:text-base sm:block">
        One or multiple cars in single trip.
      </p>
    </div>

    {/* Right: hero actions */}
    <div className="flex flex-col items-stretch gap-2 pt-3 sm:items-end sm:pt-0 lg:w-[382px] lg:justify-end">
    <button
  type="button"
  onClick={handleSearchClick}
  className="inline-flex min-h-[50px] items-center justify-between
             rounded-[18px]
             bg-[color:var(--brand-pink,#FF2D83)]
             px-5 py-3 text-[14px] font-semibold text-white
             lg:w-full
             transition
             active:scale-[0.99]
             focus-visible:outline-none
             focus-visible:ring-2 focus-visible:ring-offset-2
             focus-visible:ring-[color:var(--brand-pink,#FF2D83)]"
>
  <span className="inline-flex items-center gap-3">
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
      <path d="M7 3.5v3M17 3.5v3M3.5 9.5h17" />
    </svg>
    <span className="text-left leading-tight">
      <span className="block">Schedule Trip / Book Multiple Cars</span>
      <span className="block text-[10px] font-medium text-white/75">
        Cars for weddings, events, or daily travel
      </span>
    </span>
  </span>
  <svg
    viewBox="0 0 24 24"
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14" />
    <path d="m13 6 6 6-6 6" />
  </svg>
</button>

<button
  type="button"
  onClick={handleRideNowClick}
  className="inline-flex min-h-[50px] items-center justify-between
             rounded-[18px]
             border border-slate-300 bg-white
             px-5 py-3 text-[14px] font-extrabold text-slate-900
             lg:w-full
             shadow-[0_10px_24px_rgba(15,23,42,0.06)] transition
             hover:border-slate-400 hover:bg-slate-50
             active:scale-[0.99]
             focus-visible:outline-none
             focus-visible:ring-2 focus-visible:ring-offset-2
             focus-visible:ring-slate-300"
>
  <span className="inline-flex items-center gap-3">
    <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900 text-base text-white">
      ⚡
    </span>
    <span className="text-left leading-tight">
      <span className="block">Ride Now</span>
      <span className="block text-[10px] font-medium text-slate-500">
        Get the app for instant rides
      </span>
    </span>
  </span>
  <svg
    viewBox="0 0 24 24"
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14" />
    <path d="m13 6 6 6-6 6" />
  </svg>
</button>

    </div>
  </div>
  <div className="mx-2 mt-3 h-px bg-slate-200 sm:hidden" />
</section>



     {/* ───── FEATURED: elevated promo row ───── */}
<SectionShell
  title="Featured"
  right={
    <p className="hidden text-xs text-slate-500 md:block">
      Common ways people use Wayroot for trips.
    </p>
  }
>
  <div className="-mx-4 overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:overflow-visible sm:px-0">
    <div className="flex w-max snap-x snap-mandatory gap-5 sm:grid sm:w-auto sm:grid-cols-2 lg:grid-cols-3">
    {FEATURED.map((card) => (
      <button
        key={card.key}
        type="button"
        onClick={handleSearchClick}
        className="relative w-[min(86vw,22rem)] shrink-0 snap-start overflow-hidden rounded-2xl border border-slate-200 bg-white text-left shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-900/10 sm:w-auto sm:shrink"
      >
        {/* Image: smaller, static, no zoom */}
        <div className="relative h-32 sm:h-44 w-full">
          <img
            src={card.image}
            alt={card.title}
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
            decoding="async"
          />

          {/* Subtle bottom gradient for readability */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/70 via-black/15 to-transparent" />

          {/* Text overlay */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 px-4 pb-3 pt-6">
            <h3 className="text-sm font-semibold text-white drop-shadow-sm">
              {card.title}
            </h3>
            <p className="mt-1 text-[11px] leading-5 text-slate-100/90">
              {card.sub}
            </p>
          </div>
        </div>
      </button>
    ))}
    </div>
  </div>
</SectionShell>




{/* ───── WHY WAYROOT: images lying directly on the page ───── */}
<SectionShell
  title="Why Wayroot?"
  right={
    <p className="hidden text-xs text-slate-500 md:block">
      Built for people who plan — not just ride.
    </p>
  }
>
  <div className="mx-auto max-w-6xl">
    {(() => {
      const midpoint = Math.ceil(WHY_ITEMS.length / 2);
      const rows = [WHY_ITEMS.slice(0, midpoint), WHY_ITEMS.slice(midpoint)];

      return (
        <div className="space-y-8">
          {rows.map((row, rowIdx) => (
            <div
              key={rowIdx}
              className="-mx-4 overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              <div className="flex w-max snap-x snap-mandatory gap-5">
                {row.map((card) => (
                  <div
                    key={card.key}
                    className="flex w-[min(72vw,18rem)] shrink-0 snap-start flex-col sm:w-[min(44vw,19rem)] lg:w-[18rem] xl:w-[19rem]"
                  >
                    {/* Image: directly on page, rounded, no extra card bg */}
                    {card.image ? (
                      <img
                        src={card.image}
                        alt={card.title}
                        className="block w-full h-auto rounded-3xl"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className="flex h-32 w-full items-center justify-center rounded-3xl bg-slate-50 text-3xl text-slate-400">
                        🚗
                      </div>
                    )}

                    {/* Text area below image, on plain page */}
                    <div className="pt-3">
                      <h3 className="text-sm md:text-base font-semibold text-slate-900">
                        {card.title}
                      </h3>
                      <p className="mt-2 text-xs md:text-sm leading-relaxed text-slate-600">
                        {card.sub}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    })()}
  </div>
</SectionShell>










        </div>
      </main>

      <Footer />
    </div>
  );
}
